import React from "react";
import { toastr } from "react-redux-toastr";
import { connect } from "react-redux";
import {
	FormGroup,
	FormControl,
	FormLabel,
	FormCheck,
	Row,
	Col,
} from "react-bootstrap";
import { contractsService, authService } from "services";
import {
	errorFormating,
	normalizeDecimal,
	formatDecimalFieldValue,
	isValidDecimalInput,
	sanitizeDecimalInput,
} from "utils/utils";
import SubmitButton from "components/SubmitButton";
import InlineAlert from "components/InlineAlert";
import ContractEmployees from "components/Contracts/ContractEmployees";
import CatalogMultiSelect, {
	catalogChip,
} from "components/CatalogMultiSelect/CatalogMultiSelect";
import Switch from "components/Switch";
import UnitInput from "components/Shared/UnitInput";
import Drawer from "components/Drawer";
import { getFirstLastInterval } from "utils/datetime";
import * as userActions from "actions/userActions";
import * as shiftsActions from "actions/shiftsActions";
import * as timesActions from "actions/timesActions";
import { isHaddockEnvironment } from "utils/documentUtils";
import { withTranslation } from "react-i18next";

const DECIMAL_CONTRACT_FIELDS = [
	"target_hours_per_week",
	"vacation_day_hour_equivalence",
	"vacation_minimum_amount_required_hour",
	"vacation_maximum_amount_required_hour",
	"alert_planning_min_rest_hours",
	"time_registration_global_worked_hours_leaving_earlier",
	"time_registration_global_worked_hours_leaving_later",
	"time_registration_tolerance_arriving_earlier",
	"time_registration_tolerance_arriving_later",
	"time_registration_tolerance_leaving_earlier",
	"time_registration_tolerance_leaving_later",
	"alert_time_registration_started_too_early",
	"alert_time_registration_started_too_late",
	"alert_time_registration_ended_too_early",
	"alert_time_registration_ended_too_late",
	"alert_time_registration_started_not_done",
	"alert_planning_max_consecutive_working_days",
	"alert_break_too_short",
	"alert_break_too_long",
	"alert_time_registration_paid_break_max_duration",
	"alert_time_registration_paid_break_occurrences",
	"target_hours_on_monday",
	"target_hours_on_tuesday",
	"target_hours_on_wednesday",
	"target_hours_on_thursday",
	"target_hours_on_friday",
	"target_hours_on_saturday",
	"target_hours_on_sunday",
];

const TAB_DEFINITIONS = [
	{ eventKey: "main" },
	{ eventKey: "reglas_horas" },
	{ eventKey: "reglas_fichajes" },
	{ eventKey: "reglas_ausencias" },
	{ eventKey: "alertas" },
];

const buildInitialContract = () => ({
	name: "",
	description: "",
	target_hours_per_week: 40,
	work_days_per_week: 5,
	vacation_per_year: 30,
	vacation_type: "Días naturales",
	time_registration_override: false,
	time_registration_tolerance_correction: false,
	time_registration_tolerance_arriving_earlier: 0,
	time_registration_tolerance_arriving_later: 0,
	time_registration_tolerance_leaving_earlier: 0,
	time_registration_tolerance_leaving_later: 0,
	time_registration_global_worked_hours_correction: false,
	time_registration_global_worked_hours_leaving_earlier: 0,
	time_registration_global_worked_hours_leaving_later: 0,
	not_sum_target_hours_on_holiday: false,
	not_sum_target_hours_on_shifts_ids: [],
	temp_shift: {},
	enabledTolerance: false,
	time_registration_not_done_last_ten_days_email_reminder: false,
	time_registration_allowed_without_shift_planned: true,
	target_hours_distribution_per_week: true,
	isWeekly: true,
	vacation_day_hour_equivalence: 0,
	vacation_minimum_amount_required_hour: 0,
	vacation_maximum_amount_required_hour: 0,
	vacation_fraction_requestable_hour: 0,
	allow_manual_time_registrations: isHaddockEnvironment() ? false : true,
	block_time_registration_not_in_ubication: false,
	specific_time_types: false,
	time_type_ids: [],
	temp_time_type: {},
	keep_worked_hours_on_overriding_shifts_ids: [],
	temp_absence: {},
	alert_time_registration_started_too_early: 0,
	alert_time_registration_started_too_late: 0,
	alert_time_registration_ended_too_early: 0,
	alert_time_registration_ended_too_late: 0,
	alert_time_registration_started_not_done: 0,
	alert_planning_min_rest_hours: 0,
	alert_planning_max_consecutive_working_days: 0,
	alert_break_too_short: 0,
	alert_break_too_long: 0,
	alert_break_not_planned: false,
	alert_time_registration_paid_break_max_duration: 0,
	alert_time_registration_paid_break_occurrences: 0,
	alert_time_registration_reminder: false,
	alert_time_registration_not_in_ubication: false,
	allowed_absence_shifts_ids: [],
	temp_absence_shift: {},
	workflow_status_change_email: false,
	workflow_request_email: false,
	allow_group_planning_visualization: false,
	alert_group_changed: false,
	alert_plan_changed: false,
	allow_time_registration_token_authentication: false,
	allow_shift_pattern_overlap: false,
	allow_employee_workflow_cancel: false,
	allow_shift_exchange: false,
});

class ContractEditDrawer extends React.Component {
	constructor(props) {
		super(props);

		let contract = buildInitialContract();
		let isEdit = false;
		let selected_shifts = [];
		let selected_time_types = [];
		let selected_absences = [];
		let selected_absence_shifts = [];

		if (props.contract) {
			contract = { ...contract, ...props.contract };
			contract.allow_employee_workflow_cancel =
				contract.allow_employee_workflow_cancel || false;
			contract.allow_shift_exchange = contract.allow_shift_exchange || false;
			isEdit = true;
			contract.enabledTolerance =
				contract.time_registration_global_worked_hours_correction ||
				contract.time_registration_tolerance_correction;
			contract.isWeekly = contract.target_hours_distribution_per_week;

			if (contract.not_sum_target_hours_on_shifts) {
				selected_shifts = contract.not_sum_target_hours_on_shifts;
			}
			if (contract.specific_time_types) {
				selected_time_types = contract.time_types;
			}
			if (contract.keep_worked_hours_on_overriding_shifts) {
				selected_absences = contract.keep_worked_hours_on_overriding_shifts;
			}
			if (contract.allowed_absence_shifts) {
				selected_absence_shifts = contract.allowed_absence_shifts;
			}

			contract.alert_time_registration_started_too_early =
				contract.alert_time_registration_started_too_early || 0;
			contract.alert_time_registration_started_too_late =
				contract.alert_time_registration_started_too_late || 0;
			contract.alert_time_registration_ended_too_early =
				contract.alert_time_registration_ended_too_early || 0;
			contract.alert_time_registration_ended_too_late =
				contract.alert_time_registration_ended_too_late || 0;
			contract.alert_time_registration_started_not_done =
				contract.alert_time_registration_started_not_done || 0;
			contract.alert_planning_min_rest_hours =
				contract.alert_planning_min_rest_hours || 0;
			contract.alert_planning_max_consecutive_working_days =
				contract.alert_planning_max_consecutive_working_days || 0;
			contract.alert_break_too_short = contract.alert_break_too_short || 0;
			contract.alert_break_too_long = contract.alert_break_too_long || 0;
			contract.alert_time_registration_paid_break_max_duration =
				contract.alert_time_registration_paid_break_max_duration || 0;
			contract.alert_time_registration_paid_break_occurrences =
				contract.alert_time_registration_paid_break_occurrences || 0;
		}

		if (
			!isEdit &&
			props.non_attendance_shifts &&
			props.non_attendance_shifts.length > 0
		) {
			selected_shifts = props.non_attendance_shifts;
		}

		this.state = {
			isEdit,
			isLoading: false,
			submitted: false,
			errors: {},
			contract,
			selected_shifts,
			selected_time_types,
			selected_absences,
			selected_absence_shifts,
			new_item_errors: {},
			init_vacation_type: contract.vacation_type,
			show_vacation_type_warning: false,
			activeTab: "main",
			isDirty: false,
		};
	}

	componentDidMount() {
		if (this.state.isEdit) {
			this.getDetailedContract();
		}

		const {
			select_shifts,
			attendance_shifts,
			non_attendance_shifts,
			select_time_types,
			getSelectableShifts,
			getAttendanceShifts,
			getNonAttendanceShifts,
			getSelectableTimeTypes,
			registerBeforeClose,
		} = this.props;

		if (!select_shifts || select_shifts.length === 0) getSelectableShifts();
		if (!attendance_shifts || attendance_shifts.length === 0) getAttendanceShifts();
		if (!non_attendance_shifts || non_attendance_shifts.length === 0) getNonAttendanceShifts();
		if (!select_time_types || select_time_types.length === 0) getSelectableTimeTypes();

		if (typeof registerBeforeClose === "function") {
			this.unregisterBeforeClose = registerBeforeClose(this.beforeClose);
		}
	}

	componentWillUnmount() {
		if (typeof this.unregisterBeforeClose === "function") {
			this.unregisterBeforeClose();
		}
	}

	beforeClose = () => {
		if (!this.state.isDirty || this.state.isLoading) return true;
		// eslint-disable-next-line no-alert
		return window.confirm(
			this.props.t("contracts.form.unsavedChangesConfirm")
		);
	};

	componentDidUpdate(prevProps) {
		if (!this.state.isEdit) {
			const prevNonAttendance = prevProps.non_attendance_shifts || [];
			const currNonAttendance = this.props.non_attendance_shifts || [];
			if (
				prevNonAttendance !== currNonAttendance &&
				currNonAttendance.length > 0 &&
				this.state.selected_shifts.length === 0
			) {
				this.setState({ selected_shifts: currNonAttendance });
			}
		}
	}

	async getDetailedContract() {
		const { contract } = this.state;
		const contratoExtended = await contractsService.getAll({
			id: contract.id,
			variant: "detailed",
		});

		if (contratoExtended && contratoExtended.data && contratoExtended.data[0]) {
			let employees = contratoExtended.data[0].employees || [];
			employees.sort((a, b) => {
				const nameA = (a.name || "").toLowerCase();
				const nameB = (b.name || "").toLowerCase();
				if (nameA < nameB) return -1;
				if (nameA > nameB) return 1;
				const surnameA = (a.surname || "").toLowerCase();
				const surnameB = (b.surname || "").toLowerCase();
				if (surnameA < surnameB) return -1;
				if (surnameA > surnameB) return 1;
				return 0;
			});
			contract.employees = employees;
			this.setState({ contract });
		}
	}

	markDirty = () => {
		if (!this.state.isDirty) this.setState({ isDirty: true });
	};

	handleTabSelect = (eventKey) => {
		this.setState({ activeTab: eventKey });
	};

	handleChange = (event) => {
		let key = event.target.id;
		let value = event.target.value;

		if (DECIMAL_CONTRACT_FIELDS.includes(key)) {
			if (!isValidDecimalInput(value)) return;
			value = sanitizeDecimalInput(value);
		}

		this.setState((prev) => {
			const { contract } = prev;

			if (key === "is_work" || key === "is_natural") key = "vacation_type";
			if (key === "dayly" || key === "weekly") {
				value = key === "weekly";
				key = "isWeekly";
			}

			const nextContract = { ...contract, [key]: value };
			const nextWarning =
				key === "vacation_type"
					? String(nextContract.vacation_type) !== String(prev.init_vacation_type)
					: prev.show_vacation_type_warning;

			return {
				contract: nextContract,
				show_vacation_type_warning: nextWarning,
				isDirty: true,
			};
		});
	};

	handleSwitch = (event) => {
		const { contract } = this.state;
		this.setState({
			contract: { ...contract, [event.target.id]: event.target.checked },
			isDirty: true,
		});
	};

	checkValidField = (name) =>
		this.state.submitted &&
		this.state.errors[name] !== undefined &&
		this.state.errors[name] !== "";

	getDecimalFieldValue = (value) => formatDecimalFieldValue(value);

	parseDecimalField = (value) => {
		if (value === "" || value === null || value === undefined) return value;
		return Number(normalizeDecimal(value));
	};

	handleSubmit = async (event) => {
		if (event && event.preventDefault) event.preventDefault();

		this.setState({ isLoading: true });
		let response = "";
		const props = this.props;
		const contract = this.state.contract;
		contract.target_hours_distribution_per_week = contract.isWeekly;

		DECIMAL_CONTRACT_FIELDS.forEach((field) => {
			const rawValue = contract[field];
			if (rawValue !== "" && rawValue !== null && rawValue !== undefined) {
				contract[field] = this.parseDecimalField(rawValue);
			}
		});

		contract.time_registration_tolerance_correction =
			contract.enabledTolerance &&
			!contract.time_registration_global_worked_hours_correction;

		if (
			contract.time_registration_global_worked_hours_correction ||
			!contract.enabledTolerance
		) {
			contract.time_registration_tolerance_arriving_earlier = 0;
			contract.time_registration_tolerance_arriving_later = 0;
			contract.time_registration_tolerance_leaving_earlier = 0;
			contract.time_registration_tolerance_leaving_later = 0;
		}

		if (
			contract.time_registration_tolerance_correction ||
			!contract.enabledTolerance
		) {
			contract.time_registration_global_worked_hours_leaving_earlier = 0;
			contract.time_registration_global_worked_hours_leaving_later = 0;
		}

		contract.not_sum_target_hours_on_shifts_ids = this.state.selected_shifts.map(
			(s) => s.id
		);
		contract.keep_worked_hours_on_overriding_shifts_ids =
			this.state.selected_absences.map((a) => a.id);
		contract.allowed_absence_shifts_ids = this.state.selected_absence_shifts.map(
			(s) => s.id
		);
		if (contract.specific_time_types) {
			contract.time_type_ids = this.state.selected_time_types.map((tt) => tt.id);
		}

		response = this.state.isEdit
			? await contractsService.update(contract)
			: await contractsService.add(contract);

		if (response.ok) {
			this.setState(
				{ isLoading: false, errors: {}, isDirty: false },
				() => {
					toastr.success(this.props.t("common.success"), this.props.t("common.changesSaved"));
					if (this.state.selected_time_types.length > 0) {
						props.user.employee_with_time_types = this.state.selected_time_types;
						authService.setUser(props.user);
					}
					props.getContracts();
					this.props.update();
					props.handleClose();
				}
			);
		} else {
			this.setState({
				isLoading: false,
				submitted: true,
				errors: errorFormating(response),
			});
		}
	};

	getHoursPerDay = () => {
		const { contract, errors } = this.state;
		const days_en = [
			"monday",
			"tuesday",
			"wednesday",
			"thursday",
			"friday",
			"saturday",
			"sunday",
		];
		const days_keys = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
		const input_days = [];

		for (let i = 0; i < 7; i++) {
			const today_en = days_en[i];
			const today_key = days_keys[i];
			const value = contract[`target_hours_on_${today_en}`] || 0;
			const error = errors[`target_hours_on_${today_en}`] || 0;

			input_days.push(
				<FormGroup className="form-group" key={i}>
					<UnitInput
						id={`target_hours_on_${today_en}`}
						type="text"
						selectAllOnFocus
						value={this.getDecimalFieldValue(value)}
						onChange={this.handleChange}
						placeholder="0"
						isInvalid={this.checkValidField(`target_hours_on_${today_en}`)}
						unitLabel={this.props.t(`contracts.form.days.${today_key}`)}
						className="input-group-day"
						controlClassName="form-objetivo"
					/>
					<FormControl.Feedback type="invalid">{error}</FormControl.Feedback>
				</FormGroup>
			);
		}
		return input_days;
	};

	toggleCatalogList = (key, item) => {
		this.setState((prev) => {
			const list = prev[key] || [];
			const exists = list.some((entry) => entry.id === item.id);
			return {
				[key]: exists
					? list.filter((entry) => entry.id !== item.id)
					: list.concat(item),
				isDirty: true,
			};
		});
	};

	clearCatalogList = (key) => {
		this.setState({ [key]: [], isDirty: true });
	};

	shiftCatalogLabel = (item) => {
		const raw = getFirstLastInterval(item.time_intervals, true);
		if (typeof raw !== "string") return item.name;
		const range = raw.replace(/^🕐\s*/, "").trim();
		if (!range) return item.name;
		const parts = range.split(/\s*-\s*/);
		if (parts.length < 2) return `${item.name} ${range}`;
		const short = (value) => String(value).replace(/:00$/, "");
		return `${item.name} ${short(parts[0])}-${short(parts[1])}h`;
	};

	renderCatalog = ({ items, selected, listKey, labelOf, searchKey, error }) => {
		const t = this.props.t;
		const resolveLabel = labelOf || ((item) => item.name);
		return (
			<CatalogMultiSelect
				items={items || []}
				selected={selected || []}
				labelOf={resolveLabel}
				chipOf={(item) =>
					catalogChip(item, resolveLabel(item), t("common.delete"))
				}
				searchPlaceholder={t(searchKey)}
				onToggleItem={(item) => this.toggleCatalogList(listKey, item)}
				onClear={() => this.clearCatalogList(listKey)}
				invalid={!!error}
				error={error || null}
			/>
		);
	};

	addAllShifts = () => {
		const { non_attendance_shifts } = this.props;
		if (non_attendance_shifts && non_attendance_shifts.length > 0) {
			this.setState({ selected_shifts: non_attendance_shifts, isDirty: true });
		}
	};

	// --- Renderizado por tab ---

	renderMainTab() {
		const { contract, errors } = this.state;
		const is_work = contract.vacation_type === "Días laborables";
		const isWeekly = contract.isWeekly;
		const allowGroupVisualization = contract.allow_group_planning_visualization;
		const allowTimeRegistrationTokenAuthentication =
			contract.allow_time_registration_token_authentication;
		const allowShiftPatternOverlap = contract.allow_shift_pattern_overlap;
		const allowShiftExchange = contract.allow_shift_exchange || false;

		return (
			<>
				<FormGroup className="form-group" controlId="name">
					<FormLabel>{this.props.t("contracts.form.name")} <span className="label-required">*</span></FormLabel>
					<FormControl
						type="text"
						value={contract.name}
						onChange={this.handleChange}
						placeholder={this.props.t("contracts.form.namePlaceholder")}
						isInvalid={this.checkValidField("name")}
					/>
					<FormControl.Feedback type="invalid">{errors.name}</FormControl.Feedback>
				</FormGroup>

				<FormGroup className="form-group" controlId="description">
					<FormLabel>{this.props.t("contracts.form.description")}</FormLabel>
					<FormControl
						type="text"
						value={contract.description}
						onChange={this.handleChange}
						placeholder={this.props.t("contracts.form.descriptionPlaceholder")}
						isInvalid={this.checkValidField("description")}
					/>
					<FormControl.Feedback type="invalid">{errors.name}</FormControl.Feedback>
				</FormGroup>

				<div className="objetivo-horas">
					<FormGroup
						controlId="isWeekly"
						className="form-group group-hours objetivo-semanal"
					>
						<FormLabel className="hidden">{this.props.t("contracts.form.hoursObjectiveHidden")}</FormLabel>
						<div className="btn-choice-group">
							<FormCheck
								type="radio"
								id="weekly"
								label={this.props.t("contracts.form.weeklyObjective")}
								value={true}
								checked={isWeekly}
								bsPrefix={isWeekly ? "btn-choice active" : "btn-choice"}
								onChange={this.handleChange}
							/>
							<FormCheck
								type="radio"
								id="dayly"
								label={this.props.t("contracts.form.dailyObjective")}
								value={false}
								checked={!isWeekly}
								bsPrefix={!isWeekly ? "btn-choice active" : "btn-choice"}
								onChange={this.handleChange}
							/>
						</div>
					</FormGroup>

					{contract.isWeekly && (
						<div className="group-hours objetivo-semanal">
							<p className="form-label">{this.props.t("contracts.form.weeklyGoal")} <span className="label-required">*</span></p>
							<div className="simple-row">
								<FormGroup className="form-group">
									<UnitInput
										id="target_hours_per_week"
										type="text"
										selectAllOnFocus
										value={this.getDecimalFieldValue(contract.target_hours_per_week)}
										onChange={this.handleChange}
										placeholder="0"
										isInvalid={this.checkValidField("target_hours_per_week")}
										unitLabel={this.props.t("contracts.form.hours")}
										className="input-group-compact"
									/>
								</FormGroup>
								<FormGroup className="form-group">
									<UnitInput
										id="work_days_per_week"
										type="number"
										value={
											contract.work_days_per_week === null ||
											contract.work_days_per_week === undefined ||
											contract.work_days_per_week === ""
												? ""
												: contract.work_days_per_week
										}
										onChange={this.handleChange}
										placeholder="0"
										isInvalid={this.checkValidField("work_days_per_week")}
										unitLabel={this.props.t("contracts.form.daysUnit")}
										className="input-group-compact"
									/>
								</FormGroup>
							</div>
						</div>
					)}

					{!contract.isWeekly && (
						<div className="group-hours objetivo-diario">
							<p className="form-label">{this.props.t("contracts.form.dailyObjective")} <span className="label-required">*</span></p>
							<div className="grid-hours">{this.getHoursPerDay()}</div>
							{errors.target_hours_on_every_day && (
								<div className="invalid-feedback d-block">
									{errors.target_hours_on_every_day}
								</div>
							)}
							{errors.target_hours_distribution_per_week && (
								<div className="invalid-feedback d-block">
									{errors.target_hours_distribution_per_week}
								</div>
							)}
						</div>
					)}
				</div>

				<div className="vacaciones">
					<p className="form-label">{this.props.t("contracts.form.annualVacations")}</p>
					<div className="simple-row">
						<FormGroup className="form-group" controlId="vacation_per_year">
							<FormControl
								type="text"
								value={contract.vacation_per_year}
								onChange={this.handleChange}
								placeholder="0"
								isInvalid={this.checkValidField("vacation_per_year")}
							/>
						</FormGroup>

						<FormGroup className="form-group vacaciones-tipo" controlId="type">
							<div className="btn-choice-group">
								<FormCheck
									type="radio"
									id="is_work"
									label={this.props.t("contracts.form.workDays")}
									value="Días laborables"
									checked={is_work}
									bsPrefix={is_work ? "btn-choice active" : "btn-choice"}
									onChange={this.handleChange}
								/>
								<FormCheck
									type="radio"
									id="is_natural"
									label={this.props.t("contracts.form.naturalDays")}
									value="Días naturales"
									checked={!is_work}
									bsPrefix={!is_work ? "btn-choice active" : "btn-choice"}
									onChange={this.handleChange}
								/>
							</div>
						</FormGroup>
					</div>
					{this.state.show_vacation_type_warning && (
						<div className="alert alert-warning">
							<p className="mb0">
								⚠️ <strong>{this.props.t("common.attention")}:</strong> {this.props.t("contracts.form.vacationTypeWarning")}
								{!isHaddockEnvironment() && (
									<>
										&nbsp;
										<a
											className="link"
											href="https://help.plain.ninja/es/articles/13683815-vacaciones-naturales-y-vacaciones-laborales"
											target="_blank"
											rel="noopener noreferrer"
										>
											{this.props.t("common.moreInfo")}
										</a>
									</>
								)}
							</p>
						</div>
					)}
				</div>

				<Row>
					<Col sm={12}>
						<FormGroup
							className="form-group form-group-flex form-row-switch form-group-main"
							controlId="allow_group_planning_visualization"
						>
							<FormLabel>{this.props.t("contracts.form.groupVisualization")}</FormLabel>
							<Switch
								controlId="allow_group_planning_visualization"
								isOn={allowGroupVisualization}
								handleToggle={this.handleSwitch}
							/>
						</FormGroup>
					</Col>
					<Col sm={12}>
						<FormGroup
							className="form-group form-group-flex form-row-switch form-group-main"
							controlId="allow_time_registration_token_authentication"
						>
							<FormLabel>{this.props.t("contracts.form.allowTerminal")}</FormLabel>
							<Switch
								controlId="allow_time_registration_token_authentication"
								isOn={allowTimeRegistrationTokenAuthentication}
								handleToggle={this.handleSwitch}
							/>
						</FormGroup>
					</Col>
					<Col sm={12}>
						<FormGroup
							className="form-group form-group-flex form-row-switch form-group-main"
							controlId="allow_shift_pattern_overlap"
						>
							<FormLabel>{this.props.t("contracts.form.allowShiftPatternOverlap")}</FormLabel>
							<Switch
								controlId="allow_shift_pattern_overlap"
								isOn={allowShiftPatternOverlap}
								handleToggle={this.handleSwitch}
							/>
						</FormGroup>
					</Col>
					<Col sm={12}>
						<FormGroup
							className="form-group form-group-flex form-row-switch form-group-main"
							controlId="allow_shift_exchange"
						>
							<FormLabel>{this.props.t("contracts.form.allowShiftExchange")}</FormLabel>
							<Switch
								controlId="allow_shift_exchange"
								isOn={allowShiftExchange}
								handleToggle={this.handleSwitch}
							/>
						</FormGroup>
					</Col>
				</Row>
			</>
		);
	}

	renderHoursTab() {
		const { contract, selected_shifts, selected_absences } = this.state;
		const { select_shifts, non_attendance_shifts } = this.props;
		const notSumHoliday = contract.not_sum_target_hours_on_holiday;

		return (
			<div className="contract-horas">
				<FormGroup
					className="form-group form-group-flex form-row-switch form-group-main"
					controlId="not_sum_target_hours_on_holiday"
				>
					<FormLabel>{this.props.t("contracts.form.notSumHolidayHours")}</FormLabel>
					<Switch
						controlId="not_sum_target_hours_on_holiday"
						isOn={notSumHoliday}
						handleToggle={this.handleSwitch}
					/>
				</FormGroup>

				<div className="form-group-flex form-group-main mb20">
					<p className="mb5">{this.props.t("contracts.form.notSumShiftHoursLabel")}</p>
					{this.renderCatalog({
						items: select_shifts,
						selected: selected_shifts,
						listKey: "selected_shifts",
						labelOf: this.shiftCatalogLabel,
						searchKey: "common.catalog.searchShift",
					})}
					{selected_shifts.length === 0 &&
						non_attendance_shifts &&
						non_attendance_shifts.length > 0 && (
						<div className="row-adding-help mt10">
							<button
								className="btn-link"
								onClick={this.addAllShifts}
								type="button"
							>
								{this.props.t("contracts.form.addAllAbsenceShifts")}
							</button>
						</div>
					)}
				</div>

				<div className="form-group-flex form-group-main">
					<p className="mb5">{this.props.t("contracts.form.notRemoveWorkedHoursLabel")}</p>
					{this.renderCatalog({
						items: non_attendance_shifts,
						selected: selected_absences,
						listKey: "selected_absences",
						searchKey: "common.catalog.searchAbsence",
					})}
				</div>
			</div>
		);
	}

	renderFichajesTab() {
		const { contract, errors, selected_time_types } = this.state;
		const { attendance_shifts, select_time_types } = this.props;

		const enabledOverride = contract.time_registration_override;
		const enabledTolerance = contract.enabledTolerance;
		const enabledGlobal = contract.time_registration_global_worked_hours_correction;
		const allowFichajeSinTurno = contract.time_registration_allowed_without_shift_planned;
		const allowManualTimeRegistrations = contract.allow_manual_time_registrations;
		const blockTimeRegistrationNotInUbication =
			contract.block_time_registration_not_in_ubication;
		const specificTimeTypes = contract.specific_time_types;

		const optionAttendanceShiftList =
			attendance_shifts.length > 0 &&
			attendance_shifts.map((item, i) => (
				<option key={i} value={item.id}>
					{item.name} {getFirstLastInterval(item.time_intervals, true)}
				</option>
			));

		const timeTypeError = this.checkValidField("time_types")
			? `${errors.time_types || ""}${
					errors.time_types_value ? ` (${errors.time_types_value})` : ""
				}`
			: null;

		return (
			<div className="contract-fichajes">
				<div className="drawer-fichajes-list">
					<FormGroup
						className="form-group form-group-flex form-row-switch form-group-main"
						controlId="allow_manual_time_registrations"
					>
						<FormLabel>{this.props.t("contracts.form.allowManualTimeRegistrations")}</FormLabel>
						<Switch
							controlId="allow_manual_time_registrations"
							isOn={allowManualTimeRegistrations}
							handleToggle={this.handleSwitch}
						/>
					</FormGroup>

					{!isHaddockEnvironment() && (
						<FormGroup
							className="form-group form-group-flex form-row-switch form-group-main"
							controlId="block_time_registration_not_in_ubication"
						>
							<FormLabel>{this.props.t("contracts.form.blockNotInLocation")}</FormLabel>
							<Switch
								controlId="block_time_registration_not_in_ubication"
								isOn={blockTimeRegistrationNotInUbication}
								handleToggle={this.handleSwitch}
							/>
						</FormGroup>
					)}

					<FormGroup
						className="form-group form-group-flex form-row-switch form-group-main"
						controlId="time_registration_override"
					>
						<FormLabel>{this.props.t("contracts.form.overrideWithTimeRegistration")}</FormLabel>
						<Switch
							controlId="time_registration_override"
							isOn={enabledOverride}
							handleToggle={this.handleSwitch}
						/>
					</FormGroup>

					<FormGroup
						className="form-group form-group-flex form-row-switch form-group-main form-row-switch--with-divider"
						controlId="enabledTolerance"
					>
						<FormLabel>{this.props.t("contracts.form.roundToPlannedHours")}</FormLabel>
						<Switch
							controlId="enabledTolerance"
							isOn={enabledTolerance}
							handleToggle={this.handleSwitch}
						/>
					</FormGroup>

					{enabledTolerance && (
						<FormGroup
							className="form-group form-group-flex form-row-switch form-group-main"
							controlId="time_registration_global_worked_hours_correction"
						>
							<FormLabel>{this.props.t("contracts.form.flexibleWorkday")}</FormLabel>
							<Switch
								controlId="time_registration_global_worked_hours_correction"
								isOn={enabledGlobal}
								handleToggle={this.handleSwitch}
							/>
						</FormGroup>
					)}
				</div>

				{enabledTolerance && enabledGlobal && (
					<section className="contract-tolerancia">
						<FormGroup
							className="form-group form-group-flex form-row-switch mb0"
						>
							<FormLabel>{this.props.t("contracts.form.roundLeavingEarlier")}</FormLabel>
							<UnitInput
								id="time_registration_global_worked_hours_leaving_earlier"
								type="text"
								selectAllOnFocus
								value={this.getDecimalFieldValue(
									contract.time_registration_global_worked_hours_leaving_earlier
								)}
								onChange={this.handleChange}
								placeholder="0"
								isInvalid={this.checkValidField(
									"time_registration_global_worked_hours_leaving_earlier"
								)}
								unitLabel={this.props.t("contracts.form.minUnit")}
								className="input-group-minutes"
							/>
							<FormControl.Feedback type="invalid">
								{errors.time_registration_global_worked_hours_leaving_earlier}
							</FormControl.Feedback>
						</FormGroup>

						<FormGroup
							className="form-group form-group-flex form-row-switch"
						>
							<FormLabel>{this.props.t("contracts.form.roundLeavingLater")}</FormLabel>
							<UnitInput
								id="time_registration_global_worked_hours_leaving_later"
								type="text"
								selectAllOnFocus
								value={this.getDecimalFieldValue(
									contract.time_registration_global_worked_hours_leaving_later
								)}
								onChange={this.handleChange}
								placeholder="0"
								isInvalid={this.checkValidField(
									"time_registration_global_worked_hours_leaving_later"
								)}
								unitLabel={this.props.t("contracts.form.minUnit")}
								className="input-group-minutes"
							/>
							<FormControl.Feedback type="invalid">
								{errors.time_registration_global_worked_hours_leaving_later}
							</FormControl.Feedback>
						</FormGroup>

						<p className="text-info mt30">{this.props.t("contracts.form.globalRoundingNote")}</p>
					</section>
				)}

				{enabledTolerance && !enabledGlobal && (
					<section className="contract-tolerancia mb15">
						<FormGroup
							className="form-group form-group-flex form-row-switch mb0"
						>
							<FormLabel>{this.props.t("contracts.form.roundArrivingEarlier")}</FormLabel>
							<UnitInput
								id="time_registration_tolerance_arriving_earlier"
								type="text"
								selectAllOnFocus
								value={this.getDecimalFieldValue(
									contract.time_registration_tolerance_arriving_earlier
								)}
								onChange={this.handleChange}
								placeholder="0"
								isInvalid={this.checkValidField(
									"time_registration_tolerance_arriving_earlier"
								)}
								unitLabel={this.props.t("contracts.form.minUnit")}
								className="input-group-minutes"
							/>
							<FormControl.Feedback type="invalid">
								{errors.time_registration_tolerance_arriving_earlier}
							</FormControl.Feedback>
						</FormGroup>

						<FormGroup
							className="form-group form-group-flex form-row-switch mb0"
						>
							<FormLabel>{this.props.t("contracts.form.roundArrivingLater")}</FormLabel>
							<UnitInput
								id="time_registration_tolerance_arriving_later"
								type="text"
								selectAllOnFocus
								value={this.getDecimalFieldValue(
									contract.time_registration_tolerance_arriving_later
								)}
								onChange={this.handleChange}
								placeholder="0"
								isInvalid={this.checkValidField(
									"time_registration_tolerance_arriving_later"
								)}
								unitLabel={this.props.t("contracts.form.minUnit")}
								className="input-group-minutes"
							/>
							<FormControl.Feedback type="invalid">
								{errors.time_registration_tolerance_arriving_later}
							</FormControl.Feedback>
						</FormGroup>

						<FormGroup
							className="form-group form-group-flex form-row-switch mb0"
						>
							<FormLabel>{this.props.t("contracts.form.roundLeavingEarlier")}</FormLabel>
							<UnitInput
								id="time_registration_tolerance_leaving_earlier"
								type="text"
								selectAllOnFocus
								value={this.getDecimalFieldValue(
									contract.time_registration_tolerance_leaving_earlier
								)}
								onChange={this.handleChange}
								placeholder="0"
								isInvalid={this.checkValidField(
									"time_registration_tolerance_leaving_earlier"
								)}
								unitLabel={this.props.t("contracts.form.minUnit")}
								className="input-group-minutes"
							/>
							<FormControl.Feedback type="invalid">
								{errors.time_registration_tolerance_leaving_earlier}
							</FormControl.Feedback>
						</FormGroup>

						<FormGroup
							className="form-group form-group-flex form-row-switch mb0"
						>
							<FormLabel>{this.props.t("contracts.form.roundLeavingLater")}</FormLabel>
							<UnitInput
								id="time_registration_tolerance_leaving_later"
								type="text"
								selectAllOnFocus
								value={this.getDecimalFieldValue(
									contract.time_registration_tolerance_leaving_later
								)}
								onChange={this.handleChange}
								placeholder="0"
								isInvalid={this.checkValidField(
									"time_registration_tolerance_leaving_later"
								)}
								unitLabel={this.props.t("contracts.form.minUnit")}
								className="input-group-minutes"
							/>
							<FormControl.Feedback type="invalid">
								{errors.time_registration_tolerance_leaving_later}
							</FormControl.Feedback>
						</FormGroup>
					</section>
				)}

				<FormGroup
					className="form-group form-group-flex form-row-switch form-group-main"
					controlId="time_registration_allowed_without_shift_planned"
				>
					<FormLabel>{this.props.t("contracts.form.allowTimeRegistrationWithoutShift")}</FormLabel>
					<Switch
						controlId="time_registration_allowed_without_shift_planned"
						isOn={allowFichajeSinTurno}
						handleToggle={this.handleSwitch}
					/>
				</FormGroup>

				<FormGroup controlId="shift_id" className="form-group mt20">
					<FormLabel>
					{this.props.t("contracts.form.defaultShift")}
					{allowFichajeSinTurno && enabledOverride && (
							<span className="label-required">*</span>
						)}
					</FormLabel>
					<FormControl
						value={contract.shift_id}
						onChange={this.handleChange}
						placeholder={this.props.t("contracts.form.defaultShift")}
						isInvalid={this.checkValidField("shift_id")}
						as="select"
					>
						<option value="">{this.props.t("contracts.form.defaultShiftPlaceholder")}</option>
						{optionAttendanceShiftList}
					</FormControl>
					<FormControl.Feedback type="invalid">{errors.shift_id}</FormControl.Feedback>
				</FormGroup>

				<FormGroup
					className="form-group form-group-flex form-row-switch form-group-main"
					controlId="specific_time_types"
				>
					<FormLabel>{this.props.t("contracts.form.specificTimeTypes")}</FormLabel>
					<Switch
						controlId="specific_time_types"
						isOn={specificTimeTypes}
						handleToggle={this.handleSwitch}
					/>
				</FormGroup>

				{specificTimeTypes && (
					<div className="form-group-flex form-group-main form-group-time-type">
						{this.renderCatalog({
							items: select_time_types,
							selected: selected_time_types,
							listKey: "selected_time_types",
							searchKey: "common.catalog.searchTimeType",
							error: timeTypeError,
						})}
					</div>
				)}
			</div>
		);
	}

	renderAusenciasTab() {
		const { contract, errors, selected_absence_shifts } = this.state;
		const { non_attendance_shifts } = this.props;
		const allowEmployeeWorkflowCancel = contract.allow_employee_workflow_cancel || false;

		return (
			<div className="contract-ausencias">
				<FormGroup
					className="form-group form-group-flex form-row-switch"
				>
					<FormLabel>{this.props.t("contracts.form.vacationDayHourEquivalence")}</FormLabel>
					<UnitInput
						id="vacation_day_hour_equivalence"
						type="text"
						selectAllOnFocus
						value={this.getDecimalFieldValue(contract.vacation_day_hour_equivalence)}
						onChange={this.handleChange}
						placeholder="0"
						isInvalid={this.checkValidField("vacation_day_hour_equivalence")}
						unitLabel={this.props.t("contracts.form.hoursShort")}
						className="input-group-minutes"
					/>
					<FormControl.Feedback type="invalid">
						{errors.vacation_day_hour_equivalence}
					</FormControl.Feedback>
				</FormGroup>

				<FormGroup
					className="form-group form-group-flex form-row-switch"
				>
					<FormLabel>{this.props.t("contracts.form.minHoursAbsence")}</FormLabel>
					<UnitInput
						id="vacation_minimum_amount_required_hour"
						type="text"
						selectAllOnFocus
						value={this.getDecimalFieldValue(
							contract.vacation_minimum_amount_required_hour
						)}
						onChange={this.handleChange}
						placeholder="0"
						isInvalid={this.checkValidField(
							"vacation_minimum_amount_required_hour"
						)}
						unitLabel={this.props.t("contracts.form.hoursShort")}
						className="input-group-minutes"
					/>
					<FormControl.Feedback type="invalid">
						{errors.vacation_minimum_amount_required_hour}
					</FormControl.Feedback>
				</FormGroup>

				<FormGroup
					className="form-group form-group-flex form-row-switch"
				>
					<FormLabel>{this.props.t("contracts.form.maxHoursAbsence")}</FormLabel>
					<UnitInput
						id="vacation_maximum_amount_required_hour"
						type="text"
						selectAllOnFocus
						value={this.getDecimalFieldValue(
							contract.vacation_maximum_amount_required_hour
						)}
						onChange={this.handleChange}
						placeholder="0"
						isInvalid={this.checkValidField(
							"vacation_maximum_amount_required_hour"
						)}
						unitLabel={this.props.t("contracts.form.hoursShort")}
						className="input-group-minutes"
					/>
					<FormControl.Feedback type="invalid">
						{errors.vacation_maximum_amount_required_hour}
					</FormControl.Feedback>
				</FormGroup>

				<FormGroup
					className="form-group form-group-flex form-row-switch"
				>
					<FormLabel>{this.props.t("contracts.form.minFractionAbsence")}</FormLabel>
					<UnitInput
						id="vacation_fraction_requestable_hour"
						as="select"
						customSelect
						value={contract.vacation_fraction_requestable_hour}
						onChange={this.handleChange}
						placeholder="0"
						isInvalid={this.checkValidField("vacation_fraction_requestable_hour")}
						unitLabel={this.props.t("contracts.form.minUnit")}
						className="input-group-minutes"
					>
							<option value="">0</option>
							<option value="0.25">15</option>
							<option value="0.5">30</option>
							<option value="0.75">45</option>
					</UnitInput>
					<FormControl.Feedback type="invalid">
						{errors.vacation_fraction_requestable_hour}
					</FormControl.Feedback>
				</FormGroup>

				<hr className="mt25 mb25" />

				<div className="form-group-flex form-group-main">
					<p className="mb5">{this.props.t("contracts.form.showAbsenceShiftsLabel")}</p>
					{this.renderCatalog({
						items: non_attendance_shifts,
						selected: selected_absence_shifts,
						listKey: "selected_absence_shifts",
						searchKey: "common.catalog.searchAbsence",
					})}
				</div>

				<hr className="mt25 mb25" />

				<FormGroup
					className="form-group form-group-flex form-row-switch form-group-main"
					controlId="allow_employee_workflow_cancel"
				>
					<FormLabel>{this.props.t("contracts.form.allowEmployeeCancel")}</FormLabel>
					<Switch
						controlId="allow_employee_workflow_cancel"
						isOn={allowEmployeeWorkflowCancel}
						handleToggle={this.handleSwitch}
					/>
				</FormGroup>

			</div>
		);
	}

	renderAlertasTab() {
		const { contract, errors } = this.state;
		const enabledTimeRegistrationNotDoneReminder =
			contract.time_registration_not_done_last_ten_days_email_reminder;
		const breakNotPlanned = contract.alert_break_not_planned;
		const timeRegistrationReminder = contract.alert_time_registration_reminder;
		const timeRegistrationNotInUbication =
			contract.alert_time_registration_not_in_ubication;
		const enabledRequestEmail = contract.workflow_request_email;
		const enabledWorkflowChangeEmail = contract.workflow_status_change_email;
		const enabledGroupChangeEmail = contract.alert_group_changed;
		const enabledPlanChangeEmail = contract.alert_plan_changed;

		return (
			<div className="contract-email">
				<h3 className="subtitle">{this.props.t("contracts.form.emailsSection")}</h3>
				<div className="drawer-alerts-email-list">
					<FormGroup
						className="form-group form-group-flex form-row-switch form-group-main"
						controlId="time_registration_not_done_last_ten_days_email_reminder"
					>
						<FormLabel>{this.props.t("contracts.form.emailTimeRegistrationNotDone")}</FormLabel>
						<Switch
							controlId="time_registration_not_done_last_ten_days_email_reminder"
							isOn={enabledTimeRegistrationNotDoneReminder}
							handleToggle={this.handleSwitch}
						/>
					</FormGroup>

					<FormGroup
						className="form-group form-group-flex form-row-switch form-group-main"
						controlId="workflow_request_email"
					>
						<FormLabel>{this.props.t("contracts.form.emailWorkflowRequest")}</FormLabel>
						<Switch
							controlId="workflow_request_email"
							isOn={enabledRequestEmail}
							handleToggle={this.handleSwitch}
						/>
					</FormGroup>

					<FormGroup
						className="form-group form-group-flex form-row-switch form-group-main"
						controlId="workflow_status_change_email"
					>
						<FormLabel>{this.props.t("contracts.form.emailWorkflowChange")}</FormLabel>
						<Switch
							controlId="workflow_status_change_email"
							isOn={enabledWorkflowChangeEmail}
							handleToggle={this.handleSwitch}
						/>
					</FormGroup>

					<FormGroup
						className="form-group form-group-flex form-row-switch form-group-main"
						controlId="alert_group_changed"
					>
						<FormLabel>{this.props.t("contracts.form.emailGroupChange")}</FormLabel>
						<Switch
							controlId="alert_group_changed"
							isOn={enabledGroupChangeEmail}
							handleToggle={this.handleSwitch}
						/>
					</FormGroup>

					<FormGroup
						className="form-group form-group-flex form-row-switch form-group-main"
						controlId="alert_plan_changed"
					>
						<FormLabel>{this.props.t("contracts.form.emailPlanChange")}</FormLabel>
						<Switch
							controlId="alert_plan_changed"
							isOn={enabledPlanChangeEmail}
							handleToggle={this.handleSwitch}
						/>
					</FormGroup>

					<FormGroup
						className="form-group form-group-flex form-row-switch form-group-main"
						controlId="alert_time_registration_reminder"
					>
						<FormLabel>{this.props.t("contracts.form.emailTimeRegistrationReminder")}</FormLabel>
						<Switch
							controlId="alert_time_registration_reminder"
							isOn={timeRegistrationReminder}
							handleToggle={this.handleSwitch}
						/>
					</FormGroup>
				</div>

				<div className="drawer-alerts-stack">
					<h3 className="subtitle">{this.props.t("contracts.form.timeRegistrationAlerts")}</h3>

					<FormGroup
						className="form-group form-group-flex form-row-switch"
					>
						<FormLabel>{this.props.t("contracts.form.alertArrivingEarlier")}</FormLabel>
						<UnitInput
							id="alert_time_registration_started_too_early"
							type="text"
							selectAllOnFocus
							value={this.getDecimalFieldValue(
								contract.alert_time_registration_started_too_early
							)}
							onChange={this.handleChange}
							placeholder="0"
							isInvalid={this.checkValidField(
								"alert_time_registration_started_too_early"
							)}
							unitLabel={this.props.t("contracts.form.minUnit")}
							className="input-group-minutes"
						/>
						<FormControl.Feedback type="invalid">
							{errors.alert_time_registration_started_too_early}
						</FormControl.Feedback>
					</FormGroup>

					<FormGroup
						className="form-group form-group-flex form-row-switch"
					>
						<FormLabel>{this.props.t("contracts.form.alertArrivingLater")}</FormLabel>
						<UnitInput
							id="alert_time_registration_started_too_late"
							type="text"
							selectAllOnFocus
							value={this.getDecimalFieldValue(
								contract.alert_time_registration_started_too_late
							)}
							onChange={this.handleChange}
							placeholder="0"
							isInvalid={this.checkValidField(
								"alert_time_registration_started_too_late"
							)}
							unitLabel={this.props.t("contracts.form.minUnit")}
							className="input-group-minutes"
						/>
						<FormControl.Feedback type="invalid">
							{errors.alert_time_registration_started_too_late}
						</FormControl.Feedback>
					</FormGroup>

					<FormGroup
						className="form-group form-group-flex form-row-switch"
					>
						<FormLabel>{this.props.t("contracts.form.alertLeavingEarlier")}</FormLabel>
						<UnitInput
							id="alert_time_registration_ended_too_early"
							type="text"
							selectAllOnFocus
							value={this.getDecimalFieldValue(
								contract.alert_time_registration_ended_too_early
							)}
							onChange={this.handleChange}
							placeholder="0"
							isInvalid={this.checkValidField(
								"alert_time_registration_ended_too_early"
							)}
							unitLabel={this.props.t("contracts.form.minUnit")}
							className="input-group-minutes"
						/>
						<FormControl.Feedback type="invalid">
							{errors.alert_time_registration_ended_too_early}
						</FormControl.Feedback>
					</FormGroup>

					<FormGroup
						className="form-group form-group-flex form-row-switch"
					>
						<FormLabel>{this.props.t("contracts.form.alertLeavingLater")}</FormLabel>
						<UnitInput
							id="alert_time_registration_ended_too_late"
							type="text"
							selectAllOnFocus
							value={this.getDecimalFieldValue(
								contract.alert_time_registration_ended_too_late
							)}
							onChange={this.handleChange}
							placeholder="0"
							isInvalid={this.checkValidField(
								"alert_time_registration_ended_too_late"
							)}
							unitLabel={this.props.t("contracts.form.minUnit")}
							className="input-group-minutes"
						/>
						<FormControl.Feedback type="invalid">
							{errors.alert_time_registration_ended_too_late}
						</FormControl.Feedback>
					</FormGroup>

					<FormGroup
						className="form-group form-group-flex form-row-switch"
					>
						<FormLabel>{this.props.t("contracts.form.alertNotRegistered")}</FormLabel>
						<UnitInput
							id="alert_time_registration_started_not_done"
							type="text"
							selectAllOnFocus
							value={this.getDecimalFieldValue(
								contract.alert_time_registration_started_not_done
							)}
							onChange={this.handleChange}
							placeholder="0"
							isInvalid={this.checkValidField(
								"alert_time_registration_started_not_done"
							)}
							unitLabel={this.props.t("contracts.form.minUnit")}
							className="input-group-minutes"
						/>
						<FormControl.Feedback type="invalid">
							{errors.alert_time_registration_started_not_done}
						</FormControl.Feedback>
					</FormGroup>

					{!isHaddockEnvironment() && (
						<FormGroup
							className="form-group form-group-flex form-row-switch form-group-main"
							controlId="alert_time_registration_not_in_ubication"
						>
							<FormLabel>{this.props.t("contracts.form.alertNotInUbication")}</FormLabel>
							<Switch
								controlId="alert_time_registration_not_in_ubication"
								isOn={timeRegistrationNotInUbication}
								handleToggle={this.handleSwitch}
							/>
						</FormGroup>
					)}

					<h3 className="subtitle mt20">{this.props.t("contracts.form.planningAlerts")}</h3>

					<FormGroup
						className="form-group form-group-flex form-row-switch"
					>
						<FormLabel>{this.props.t("contracts.form.alertMinRestHours")}</FormLabel>
						<UnitInput
							id="alert_planning_min_rest_hours"
							type="text"
							selectAllOnFocus
							value={this.getDecimalFieldValue(contract.alert_planning_min_rest_hours)}
							onChange={this.handleChange}
							placeholder="0"
							isInvalid={this.checkValidField("alert_planning_min_rest_hours")}
							unitLabel={this.props.t("contracts.form.hours")}
							className="input-group-minutes"
						/>
						<FormControl.Feedback type="invalid">
							{errors.alert_planning_min_rest_hours}
						</FormControl.Feedback>
					</FormGroup>

					<FormGroup
						className="form-group form-group-flex form-row-switch"
					>
						<FormLabel>{this.props.t("contracts.form.alertMaxConsecutiveDays")}</FormLabel>
						<UnitInput
							id="alert_planning_max_consecutive_working_days"
							type="text"
							selectAllOnFocus
							value={this.getDecimalFieldValue(
								contract.alert_planning_max_consecutive_working_days
							)}
							onChange={this.handleChange}
							placeholder="0"
							isInvalid={this.checkValidField(
								"alert_planning_max_consecutive_working_days"
							)}
							unitLabel={this.props.t("contracts.form.daysUnit")}
							className="input-group-minutes"
						/>
						<FormControl.Feedback type="invalid">
							{errors.alert_time_registration_started_not_done}
						</FormControl.Feedback>
					</FormGroup>

					<h3 className="subtitle mt20">{this.props.t("contracts.form.breakAlerts")}</h3>

					<FormGroup
						className="form-group form-group-flex form-row-switch"
					>
						<FormLabel>{this.props.t("contracts.form.alertMinBreak")}</FormLabel>
						<UnitInput
							id="alert_break_too_short"
							type="text"
							selectAllOnFocus
							value={this.getDecimalFieldValue(contract.alert_break_too_short)}
							onChange={this.handleChange}
							placeholder="0"
							isInvalid={this.checkValidField("alert_break_too_short")}
							unitLabel={this.props.t("contracts.form.minUnit")}
							className="input-group-minutes"
						/>
						<FormControl.Feedback type="invalid">
							{errors.alert_break_too_short}
						</FormControl.Feedback>
					</FormGroup>

					<FormGroup
						className="form-group form-group-flex form-row-switch"
					>
						<FormLabel>{this.props.t("contracts.form.alertMaxBreak")}</FormLabel>
						<UnitInput
							id="alert_break_too_long"
							type="text"
							selectAllOnFocus
							value={this.getDecimalFieldValue(contract.alert_break_too_long)}
							onChange={this.handleChange}
							placeholder="0"
							isInvalid={this.checkValidField("alert_break_too_long")}
							unitLabel={this.props.t("contracts.form.minUnit")}
							className="input-group-minutes"
						/>
						<FormControl.Feedback type="invalid">
							{errors.alert_break_too_long}
						</FormControl.Feedback>
					</FormGroup>

					<FormGroup
						className="form-group form-group-flex form-row-switch form-group-main"
						controlId="alert_break_not_planned"
					>
						<FormLabel>{this.props.t("contracts.form.alertBreakNotPlanned")}</FormLabel>
						<Switch
							controlId="alert_break_not_planned"
							isOn={breakNotPlanned}
							handleToggle={this.handleSwitch}
						/>
					</FormGroup>

					<h3 className="subtitle mt20">{this.props.t("contracts.form.paidBreakAlerts")}</h3>

					<FormGroup
						className="form-group form-group-flex form-row-switch"
					>
						<FormLabel>{this.props.t("contracts.form.alertPaidBreakMaxDuration")}</FormLabel>
						<UnitInput
							id="alert_time_registration_paid_break_max_duration"
							type="text"
							selectAllOnFocus
							value={this.getDecimalFieldValue(
								contract.alert_time_registration_paid_break_max_duration
							)}
							onChange={this.handleChange}
							placeholder="0"
							isInvalid={this.checkValidField(
								"alert_time_registration_paid_break_max_duration"
							)}
							unitLabel={this.props.t("contracts.form.minUnit")}
							className="input-group-minutes"
						/>
						<FormControl.Feedback type="invalid">
							{errors.alert_time_registration_paid_break_max_duration}
						</FormControl.Feedback>
					</FormGroup>

					<FormGroup
						className="form-group form-group-flex form-row-switch"
					>
						<FormLabel>{this.props.t("contracts.form.alertPaidBreakOccurrences")}</FormLabel>
						<UnitInput
							id="alert_time_registration_paid_break_occurrences"
							type="text"
							selectAllOnFocus
							value={this.getDecimalFieldValue(
								contract.alert_time_registration_paid_break_occurrences
							)}
							onChange={this.handleChange}
							placeholder="0"
							isInvalid={this.checkValidField(
								"alert_time_registration_paid_break_occurrences"
							)}
							unitLabel={this.props.t("contracts.form.timesUnit")}
							className="input-group-minutes"
						/>
						<FormControl.Feedback type="invalid">
							{errors.alert_time_registration_paid_break_occurrences}
						</FormControl.Feedback>
					</FormGroup>
				</div>
			</div>
		);
	}

	renderEmpleadosTab() {
		return (
			<div className="contract-empleados">
				<ContractEmployees
					getContracts={this.props.getContracts}
					contract={this.state.contract}
					useFullHeight
				/>
			</div>
		);
	}

	render() {
		const { isEdit, activeTab, errors, contract } = this.state;
		const { t } = this.props;

		const TAB_LABELS = {
			main: t("contracts.form.tabs.general"),
			reglas_horas: t("contracts.form.tabs.hoursWorked"),
			reglas_fichajes: t("contracts.form.tabs.timeTracking"),
			reglas_ausencias: t("contracts.form.tabs.absences"),
			alertas: t("contracts.form.tabs.alerts"),
			empleados: t("contracts.form.tabs.viewEmployees"),
		};

		const tabs = isEdit
			? [...TAB_DEFINITIONS, { eventKey: "empleados" }]
			: TAB_DEFINITIONS;

		const currentTab = tabs.find((tab) => tab.eventKey === activeTab) || tabs[0];

		return (
			<Drawer.Tabs activeKey={activeTab} onSelect={this.handleTabSelect}>
				<Drawer.Sidebar>
					<Drawer.SidebarHeader title={isEdit ? this.props.t("contracts.list.editContract") : this.props.t("contracts.newContract")} subtitle={contract.name || undefined} />
					<Drawer.Nav>
						{tabs.map((tab) => (
							<Drawer.NavItem key={tab.eventKey} eventKey={tab.eventKey}>
								{TAB_LABELS[tab.eventKey]}
							</Drawer.NavItem>
						))}
					</Drawer.Nav>
				</Drawer.Sidebar>

				<Drawer.Main>
					<Drawer.MainHeader title={TAB_LABELS[currentTab.eventKey]} />

					<Drawer.Body className="contract-edit contract-edit-drawer">
						<Drawer.TabPane eventKey="main">{this.renderMainTab()}</Drawer.TabPane>
						<Drawer.TabPane eventKey="reglas_horas">{this.renderHoursTab()}</Drawer.TabPane>
						<Drawer.TabPane eventKey="reglas_fichajes">{this.renderFichajesTab()}</Drawer.TabPane>
						<Drawer.TabPane eventKey="reglas_ausencias">{this.renderAusenciasTab()}</Drawer.TabPane>
						<Drawer.TabPane eventKey="alertas">{this.renderAlertasTab()}</Drawer.TabPane>
						{isEdit && (
							<Drawer.TabPane eventKey="empleados">{this.renderEmpleadosTab()}</Drawer.TabPane>
						)}

						{Object.keys(errors).length > 0 && (
							<InlineAlert
								variant="error"
								className="alert-footer mt20"
								message={this.props.t("common.configValidationFailed")}
								details={
									<ul className="inline-alert__list">
										{Object.entries(errors)
											.filter(([, value]) => value)
											.map(([key, value]) => (
												<li key={key}>{value}</li>
											))}
									</ul>
								}
							/>
						)}
					</Drawer.Body>

					<Drawer.Footer>
						<button
							type="button"
							onClick={this.props.handleClose}
							className="btn btn-outline-primary"
						>
						{this.props.t("common.cancel")}
						</button>
						<SubmitButton
							type="button"
							onClick={this.handleSubmit}
							isLoading={this.state.isLoading}
							text={this.props.t("common.save")}
							loadingText={this.props.t("common.saving")}
						/>
					</Drawer.Footer>
				</Drawer.Main>
			</Drawer.Tabs>
		);
	}
}

const mapStateToProps = (reducers) => ({
	...reducers.rulesReducer,
	...reducers.shiftsReducer,
	...reducers.timesReducer,
	...reducers.authReducer,
	user: reducers.authReducer.user,
});

const mapDispatchToProps = (dispatch) => ({
	update: () => dispatch(userActions.update()),
	getSelectableShifts: () => dispatch(shiftsActions.getSelectableShifts()),
	getAttendanceShifts: () => dispatch(shiftsActions.getAttendanceShifts()),
	getNonAttendanceShifts: () => dispatch(shiftsActions.getNonAttendanceShifts()),
	getSelectableTimeTypes: () => dispatch(timesActions.getSelectableTimeTypes()),
});

export default connect(mapStateToProps, mapDispatchToProps)(withTranslation()(ContractEditDrawer));
